/**
 * The wire shapes `Rws.AiInsights.Host.Audit.AuditEndpoints` returns (phase 16, `AIA-81`): the four
 * `UR-X-02` dimensions (by actor, by approver, by vendor, by period), `UR-X-03`'s chain-verification
 * check, and `UR-X-04`'s point-in-time entitlement reconstruction. Read-only; gated on `audit.read`.
 */
import { adminRequest, type AdminResult } from "./adminApi";

/** One audit entry. */
export interface AuditEntry {
  sequence: number;
  occurredAt: string;
  actorId: string | null;
  actorKind: string;
  actorEmail: string | null;
  action: string;
  subjectKind: string;
  subjectId: string;
  subjectEmail: string | null;
  subjectVendorId: string | null;
  detail: unknown;
  previousHash: string | null;
  entryHash: string;
  /** The actor's display name, when they are a person and it is known. */
  actorName?: string | null;
  /** The subject's display name, when it is a person and it is known. */
  subjectName?: string | null;
  /** The channel the action came through (the MCP server, say), or null for the website. */
  channel?: string | null;
  /** The client application that sent it, or null. */
  clientId?: string | null;
  /** The command the entry belongs to, or null. */
  correlation?: string | null;
}

/** One page of `GET /api/entitlement/audit/entries` (`AuditEntryPageResponse`): newest first, with how many match in all. */
export interface AuditEntryPage {
  items: AuditEntry[];
  totalCount: number;
  page: number;
  pageSize: number;
}

/** The search's filters and its page: text the actor, subject or vendor contains, and one recorded action matched exactly. */
export interface AuditSearch {
  search?: string;
  action?: string;
  from?: string;
  to?: string;
  page: number;
  pageSize: number;
}

/** The outcome of a chain-verification check. */
export interface AuditChainVerification {
  isIntact: boolean;
  entriesChecked: number;
  firstBrokenSequence: number | null;
  failure: string | null;
  detail: string | null;
}

/** One vendor's entitlement as reconstructed at a past instant. */
export interface ReconstructedVendorEntitlement {
  vendorId: string;
  [key: string]: unknown;
}

/** Common filters every `UR-X-02` dimension query accepts. */
export interface AuditQueryFilters {
  from?: string;
  to?: string;
  afterSequence?: number;
  limit?: number;
}

function buildQuery(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") {
      search.set(key, String(value));
    }
  }

  const query = search.toString();
  return query ? `?${query}` : "";
}

/** Calls `GET /api/entitlement/audit/by-actor`. */
export async function fetchAuditByActor(actorId: string, filters: AuditQueryFilters = {}): Promise<AdminResult<AuditEntry[]>> {
  const query = buildQuery({ actorId, ...filters });
  return adminRequest<AuditEntry[]>(`/api/entitlement/audit/by-actor${query}`, { method: "GET" }, "audit by actor");
}

/** Calls `GET /api/entitlement/audit/by-approver`. */
export async function fetchAuditByApprover(approverId: string, filters: AuditQueryFilters = {}): Promise<AdminResult<AuditEntry[]>> {
  const query = buildQuery({ approverId, ...filters });
  return adminRequest<AuditEntry[]>(`/api/entitlement/audit/by-approver${query}`, { method: "GET" }, "audit by approver");
}

/** Calls `GET /api/entitlement/audit/by-vendor`. */
export async function fetchAuditByVendor(vendorId: string, filters: AuditQueryFilters = {}): Promise<AdminResult<AuditEntry[]>> {
  const query = buildQuery({ vendorId, ...filters });
  return adminRequest<AuditEntry[]>(`/api/entitlement/audit/by-vendor${query}`, { method: "GET" }, "audit by vendor");
}

/** Calls `GET /api/entitlement/audit/by-period`. */
export async function fetchAuditByPeriod(filters: AuditQueryFilters = {}): Promise<AdminResult<AuditEntry[]>> {
  const query = buildQuery({ ...filters });
  return adminRequest<AuditEntry[]>(`/api/entitlement/audit/by-period${query}`, { method: "GET" }, "audit by period");
}

/** Calls `GET /api/entitlement/audit/entries`: the trail newest first, one page at a time, filtered on the server. */
export async function fetchAuditEntries(search: AuditSearch, signal?: AbortSignal): Promise<AdminResult<AuditEntryPage>> {
  const query = buildQuery({ search: search.search?.trim(), action: search.action, from: search.from, to: search.to, page: search.page, pageSize: search.pageSize });
  return adminRequest<AuditEntryPage>(`/api/entitlement/audit/entries${query}`, { method: "GET", signal }, "audit entries");
}

/** Calls `GET /api/entitlement/audit/actions`: every action the trail has recorded, for the action filter. */
export async function fetchAuditActions(): Promise<AdminResult<string[]>> {
  return adminRequest<string[]>("/api/entitlement/audit/actions", { method: "GET" }, "audit actions");
}

/** Calls `GET /api/entitlement/audit/chain-verification`. */
export async function fetchAuditChainVerification(): Promise<AdminResult<AuditChainVerification>> {
  return adminRequest<AuditChainVerification>("/api/entitlement/audit/chain-verification", { method: "GET" }, "chain verification");
}

